// react_frontend/src/Components/Modules/HR/ProjectList.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Grid, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Skeleton,
  Alert, LinearProgress, Stack, Avatar, Divider
} from "@mui/material";
import {
  FaPlus, FaEdit, FaTrash, FaFolderOpen, FaCheckCircle,
  FaClock, FaPlayCircle, FaArchive, FaEye, FaSync, FaUserTie
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const ProjectCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: "white",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    borderColor: "#667eea",
  },
}));

const StatusChip = styled(Chip)(({ status }) => {
  const colors = {
    planning: { bg: "#e3f2fd", color: "#1976d2" },
    active: { bg: "#e8f5e9", color: "#2e7d32" },
    completed: { bg: "#f3e5f5", color: "#7b1fa2" },
    archived: { bg: "#f5f5f5", color: "#757575" },
  };
  const c = colors[status] || colors.planning;
  return { bgcolor: c.bg, color: c.color, fontWeight: 600, fontSize: "0.75rem" };
});

const ProjectList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [users, setUsers] = useState([]); 
  const [form, setForm] = useState({
    name: "", description: "", status: "planning", 
    start_date: "", end_date: "", assigned_to: ""
  });

  useEffect(() => {
    fetchProjects();
    fetchUsers(); 
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("hr/projects");
      setProjects(res.data.data || []);
      setStats(res.data.stats || {});
    } catch (err) {
      Swal.fire("Error", "Failed to load projects", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await AxiosAuthInstance.get("hr/projects/users/assignable");
      setUsers(res.data.data || []);
    } catch (err) {
      console.error("Failed to load users", err);
    }
  };

  const openDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setForm({
        name: project.name,
        description: project.description || "",
        status: project.status,
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        assigned_to: project.assigned_to || "", // ✅
      });
    } else {
      setEditingProject(null);
      setForm({ name: "", description: "", status: "planning", start_date: "", end_date: "", assigned_to: "" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      Swal.fire("Warning", "Project name is required", "warning");
      return;
    }
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      if (editingProject) {
        await AxiosAuthInstance.put(`hr/projects/${editingProject.id}`, payload);
        Swal.fire({ icon: "success", title: "Updated!", timer: 1500, showConfirmButton: false });
      } else {
        await AxiosAuthInstance.post("hr/projects", payload);
        Swal.fire({ icon: "success", title: "Created!", timer: 1500, showConfirmButton: false });
      }
      setDialogOpen(false);
      fetchProjects();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Operation failed", "error");
    }
  };

  const handleDelete = async (project) => {
    const result = await Swal.fire({
      title: "Delete Project?",
      text: `Delete "${project.name}" and all its tasks?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      confirmButtonText: "Yes, Delete",
    });
    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`hr/projects/${project.id}`);
        Swal.fire("Deleted!", "Project deleted", "success");
        fetchProjects();
      } catch (err) {
        Swal.fire("Error", "Failed to delete", "error");
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "planning": return <FaClock />;
      case "active": return <FaPlayCircle />;
      case "completed": return <FaCheckCircle />;
      case "archived": return <FaArchive />;
      default: return <FaFolderOpen />;
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Projects" />

      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaFolderOpen style={{ marginRight: 10, verticalAlign: "middle" }} />
              Project Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage your projects and track team progress
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            onClick={() => openDialog()}
            sx={{
              bgcolor: "white", color: "#667eea", fontWeight: 600,
              "&:hover": { bgcolor: "#f0f0f0" },
              px: 3, py: 1.5, borderRadius: 2, textTransform: "none",
            }}
          >
            New Project
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
          {[
            { label: "Total", value: stats.total || 0, icon: <FaFolderOpen /> },
            { label: "Active", value: stats.active || 0, icon: <FaPlayCircle /> },
            { label: "Completed", value: stats.completed || 0, icon: <FaCheckCircle /> },
            { label: "Planning", value: stats.planning || 0, icon: <FaClock /> },
          ].map((s, i) => (
            <Paper key={i} sx={{ p: 2, bgcolor: "rgba(255,255,255,0.15)", color: "white", borderRadius: 2, minWidth: 120, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>{s.icon}<Typography variant="body2">{s.label}</Typography></Box>
              <Typography variant="h4" fontWeight={700}>{s.value}</Typography>
            </Paper>
          ))}
        </Box>
      </GradientPaper>

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : projects.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center" }}>
          <FaFolderOpen style={{ fontSize: 64, color: "#bdbdbd", marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No projects yet</Typography>
          <Typography variant="body2" color="text.secondary">Create your first project to get started</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} lg={4} key={project.id}>
              <ProjectCard onClick={() => navigate(`/hr/projects/${project.id}`)}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={700} noWrap>{project.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {project.created_by_name || "Unknown"}
                    </Typography>
                  </Box>
                  <StatusChip label={project.status} status={project.status} icon={getStatusIcon(project.status)} />
                </Box>

                {project.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxHeight: 40, overflow: "hidden" }}>
                    {project.description}
                  </Typography>
                )}

                {/* ✅ Project Lead Display */}
                {project.assignee_name && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1, bgcolor: "#f0f4ff", borderRadius: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: "#667eea", fontSize: "0.75rem" }}>
                      {project.assignee_name.charAt(0)}
                    </Avatar>
                    <Typography variant="caption" fontWeight={600} color="#4338ca">
                      Lead: {project.assignee_name}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Progress</Typography>
                    <Typography variant="caption" fontWeight={700}>{project.progress}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={project.progress}
                    sx={{
                      height: 8, borderRadius: 4, bgcolor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: project.progress === 100 ? "#4caf50" : project.progress > 50 ? "#2196f3" : "#ff9800",
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  <Chip size="small" label={`${project.stats?.todo || 0} Todo`} sx={{ bgcolor: "#f5f5f5" }} />
                  <Chip size="small" label={`${project.stats?.in_progress || 0} Active`} sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }} />
                  <Chip size="small" label={`${project.stats?.done || 0} Done`} sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }} />
                </Box>

                {(project.start_date || project.end_date) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {project.start_date && `Start: ${project.start_date}`}
                    {project.end_date && ` • End: ${project.end_date}`}
                  </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mt: 2 }}
                  onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openDialog(project)} sx={{ color: "#ff9800" }}>
                      <FaEdit size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(project)} sx={{ color: "#f44336" }}>
                      <FaTrash size={14} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ProjectCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white" }}>
          {editingProject ? "Edit Project" : "Create New Project"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth label="Project Name *" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              fullWidth label="Description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline rows={3}
            />

            {/* ✅ Project Lead (Assign To) Dropdown */}
            <FormControl fullWidth>
              <InputLabel>Project Lead (Optional)</InputLabel>
              <Select
                value={form.assigned_to}
                label="Project Lead (Optional)"
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                startAdornment={
                  <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                    <FaUserTie style={{ color: "#757575" }} />
                  </Box>
                }
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name} {u.email && `(${u.email})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status *</InputLabel>
              <Select value={form.status} label="Status *"
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="planning">Planning</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Start Date" type="date" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="End Date" type="date" value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}
            sx={{ bgcolor: "#667eea", "&:hover": { bgcolor: "#5568d3" } }}>
            {editingProject ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectList;